import { useMutation } from '@apollo/client';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    Body1,
    Button,
    Checkbox,
    HorizontalDivider,
    Modal,
    Stack,
} from '@myphx-engineering/phx-design-system';
import { constants as dsConstants } from '@myphx-engineering/phx-design-system/framework';

import {
    CancelPrescriberOrderModificationDocument,
    type DigitallyReceivedPrescriberOrderInfoFragment,
} from '../../../graphql/generated/graphql';
import { useRefetchQueries } from '../../../hooks/use-refetch-queries';
import { getRefetchQueryForPrescription } from '../../../util/get-refetch-query-for-prescription';

export type OnHoldModalProps = {
    order: DigitallyReceivedPrescriberOrderInfoFragment;
    patientId: string;
    show: boolean;
    toggle: () => void;
};

export const OnHoldModal: FC<OnHoldModalProps> = ({
    order,
    patientId,
    show,
    toggle,
}) => {
    const [isConfirmChecked, setIsConfirmChecked] = useState(false);
    const { t } = useTranslation();

    const { refetch, isLoading: isRefetchLoading } = useRefetchQueries(
        getRefetchQueryForPrescription(order.drxId ?? '', patientId)
    );

    const [CancelChangeRequest, { loading: isCancelLoading }] = useMutation(
        CancelPrescriberOrderModificationDocument
    );

    const handleConfirm = async () => {
        await CancelChangeRequest({
            variables: {
                prescriberOrderId: order.drxId ?? '',
            },
        });
        await refetch();
        toggle();
    };

    return (
        <Modal
            title={t('previewDetails.onHoldModal.title')}
            opened={show}
            onClose={toggle}
        >
            <Stack>
                <Body1>
                    {t('previewDetails.onHoldModal.line1', {
                        reg: dsConstants.unicode.REGISTERED,
                    })}
                </Body1>
                <Body1>{t('previewDetails.onHoldModal.line2')}</Body1>
                <HorizontalDivider />
                <Checkbox
                    checked={isConfirmChecked}
                    onChange={setIsConfirmChecked}
                    label={
                        <Body1>
                            {t('previewDetails.onHoldModal.confirmation')}
                        </Body1>
                    }
                />
                <Button
                    disabled={!isConfirmChecked}
                    loading={isCancelLoading || isRefetchLoading}
                    onClick={handleConfirm}
                >
                    {t('previewDetails.onHoldModal.confirm')}
                </Button>
            </Stack>
        </Modal>
    );
};
